import { currencyFormatter } from "@common/helpers/currency";
import { fullDateFullYearFormat } from "@common/helpers/date";
import { numberFormatter } from "@common/helpers/number";
import { isObject } from "@common/helpers/typeGuards";
import { formatDateOnly } from "@helpers/date";
import type { DataGridResolvedColumnMeta } from "../columns/types";
import type {
    DataGridEntity,
    DataGridFinancialAccountValue,
    DataGridLedgerAccountValue,
} from "./cellValueTypes";
import { asDateOnly } from "./dateOnlyValue";
import {
    formatDateOnlyRange,
    isPeopleValue,
    asDateOnlyRange,
    toPeopleLabel,
} from "./listDisplayValues";

const isNullableString = <TValue>(value: TValue) =>
    value === null || typeof value === "string";

/** The column fields that change how a value reads as text. */
export type DataGridDisplayTextMeta = Pick<
    DataGridResolvedColumnMeta,
    "kind" | "showYear" | "showPositive"
>;

const MAX_CACHED_TEXTS = 50_000;

/** Search formats every row's value on each keystroke, so results are cached. */
const createCachedFormat = (format: (value: number) => string) => {
    const texts = new Map<number, string>();
    return (value: number): string => {
        // Map keys treat -0 as 0, so -0 formats as 0 rather than claiming 0's entry.
        const key = value === 0 ? 0 : value;
        const cached = texts.get(key);
        if (cached !== undefined) {
            return cached;
        }
        if (texts.size >= MAX_CACHED_TEXTS) {
            texts.clear();
        }
        const text = format(key);
        texts.set(key, text);
        return text;
    };
};

type NumberFormat = "number" | "money" | "signedMoney";

const toNumberFormat = (
    meta: DataGridDisplayTextMeta | undefined,
): NumberFormat | null => {
    if (meta?.kind === "number") {
        return "number";
    }
    if (meta?.kind === "money") {
        return meta.showPositive ? "signedMoney" : "money";
    }
    return null;
};

/** Like `Amount` with `showNegative`: "- $1,250.50", rounded to the cent. */
const formatMoney =
    (positiveSign: string) =>
    (value: number): string => {
        const cents = Math.sign(value) * Math.round(Math.abs(value) * 100);
        const amount = currencyFormatter.format(Math.abs(cents) / 100);
        if (cents < 0) {
            return `- ${amount}`;
        }
        return cents > 0 ? `${positiveSign}${amount}` : amount;
    };

const formatShownNumber: Record<NumberFormat, (value: number) => string> = {
    number: createCachedFormat((value) => numberFormatter.format(value)),
    money: createCachedFormat(formatMoney("")),
    signedMoney: createCachedFormat(formatMoney("+ ")),
};

const joinSearchTexts = (texts: readonly string[]) =>
    [...new Set(texts)].join("\n");

const withMoneyVariants = (format: NumberFormat) =>
    createCachedFormat((value) =>
        joinSearchTexts([
            formatShownNumber[format](value),
            currencyFormatter.format(value),
            value.toFixed(2),
        ]),
    );

/** The shown text plus the compact ("-$1,250.50") and bare ("-1250.50") forms. */
const formatSearchNumber: Record<NumberFormat, (value: number) => string> = {
    number: createCachedFormat((value) =>
        joinSearchTexts([formatShownNumber.number(value), String(value)]),
    ),
    money: withMoneyVariants("money"),
    signedMoney: withMoneyVariants("signedMoney"),
};

/** A `DateOnlyRange` as date range cells show it ("Jan 1 – Mar 31"); `null` for anything else. */
export const toDateRangeText = <TValue>(value: TValue): string | null => {
    const range = asDateOnlyRange(value);
    return range === null
        ? null
        : formatDateOnlyRange({ range, currentYear: new Date().getFullYear() });
};

/**
 * Narrows a cell value to the text its cell shows: objects with a `name`
 * show the name; with `meta`, date columns show the formatted day
 * ("Jun 30"), number and money columns the formatted value, date ranges
 * "Jan 1 – Mar 31", and people their labels. Timestamps have no text here.
 */
export const toDisplayText = <TValue>(
    value: TValue,
    meta?: DataGridDisplayTextMeta,
): string | null => {
    const numberFormat = toNumberFormat(meta);
    if (numberFormat !== null) {
        const number = asFiniteNumber(value);
        return number === null ? null : formatShownNumber[numberFormat](number);
    }
    if (meta?.kind === "date") {
        const dateOnly = asDateOnly(value);
        const dateFormat = meta.showYear ? fullDateFullYearFormat : undefined;
        return dateOnly === null ? null : formatDateOnly(dateOnly, dateFormat);
    }
    // The day a timestamp shows depends on its row's zone, which meta doesn't carry.
    if (meta?.kind === "timestamp") {
        return null;
    }
    if (meta?.kind === "dateRange") {
        return toDateRangeText(value);
    }
    if (meta?.kind === "people") {
        return isPeopleValue(value) && value.length > 0
            ? value.map(toPeopleLabel).join(", ")
            : null;
    }
    if (typeof value === "string") {
        return value === "" ? null : value;
    }
    if (typeof value === "number") {
        return Number.isFinite(value) ? String(value) : null;
    }
    if (isObject(value) && "name" in value && typeof value.name === "string") {
        return value.name === "" ? null : value.name;
    }
    return null;
};

/**
 * What search matches a cell against: its display text, plus other ways to
 * type a number or amount, so "- $1,250.50" also matches "-$1,250" and
 * "1250.50".
 * Highlights only ever mark the display text.
 */
export const toSearchText = <TValue>(
    value: TValue,
    meta?: DataGridDisplayTextMeta,
): string | null => {
    const numberFormat = toNumberFormat(meta);
    if (numberFormat !== null) {
        const number = asFiniteNumber(value);
        return number === null
            ? null
            : formatSearchNumber[numberFormat](number);
    }
    return toDisplayText(value, meta);
};

export const isFiniteNumber = <TValue>(
    value: TValue | number,
): value is number => typeof value === "number" && Number.isFinite(value);

export const asFiniteNumber = <TValue>(value: TValue): number | null =>
    isFiniteNumber(value) ? value : null;

export const isEntityValue = <TValue>(
    value: TValue,
): value is TValue & DataGridEntity =>
    isObject(value) &&
    "name" in value &&
    isNullableString(value.name) &&
    "iconColor" in value &&
    typeof value.iconColor === "string" &&
    "logo" in value &&
    isNullableString(value.logo);

export const isLedgerAccountValue = <TValue>(
    value: TValue,
): value is TValue & DataGridLedgerAccountValue =>
    isObject(value) &&
    "name" in value &&
    typeof value.name === "string" &&
    "codeText" in value &&
    isNullableString(value.codeText);

export const isFinancialAccountValue = <TValue>(
    value: TValue,
): value is TValue & DataGridFinancialAccountValue =>
    isObject(value) &&
    "name" in value &&
    isNullableString(value.name) &&
    "accountNumberMask" in value &&
    isNullableString(value.accountNumberMask) &&
    "institutionIcon" in value &&
    (value.institutionIcon === null ||
        typeof value.institutionIcon === "object");
